import { z } from 'zod';
import { attachmentListSchema } from './attachment';
import { cardApprovalsSchema } from './card-approval';
import { cardAgingFieldsSchema } from './card-aging';
import { cardSchema } from './card';
import { cardCoverSchema } from './card-cover';
import { dueReminderOffsetSchema } from './card-dates';
import { cardLocationSchema } from './card-location';
import { checklistSchema } from './checklist';
import { cardCustomFieldSchema } from './custom-field';
import { ulidSchema } from './id';
import { cardLabelSchema } from './label';
import { LIST_TITLE_MAX_LENGTH } from './list';
import { placedStickerListSchema } from './sticker';
import { publicProfileSchema } from './user';

/**
 * The card back — the contract layer for block **B14**.
 *
 * `BE-14.1` (`FR-CARD-001`, open a card into a detail view and close it again —
 * one read, `GET /cards/:id`), `BE-14.2` (`FR-CARD-003`, the rich-text
 * description, patched field-scoped) and `BE-14.3` (`FR-CARD-007` join and
 * leave a card, `FR-CARD-008` assign other members to it). Also, from B24
 * stage 1, `startAt`/`dueAt`/`dueReminderMinutesBefore`, and from **B24 stage
 * 2**, `completed` (`BE-24.4`, `FR-CARD-004`) — see the correction below,
 * where this file's own header had predicted the field would arrive
 * "later."
 *
 * ---------------------------------------------------------------------------
 * ⚠ `FR-CARD-002` IS IN THIS BLOCK AND CONTRIBUTED NOT ONE SHAPE TO THIS FILE.
 * READ THIS BEFORE ADDING A TITLE PATCH BODY BELOW
 * ---------------------------------------------------------------------------
 * `FR-CARD-002` ("Edit the card title", the inline field at the top of the card
 * back) is a B14 requirement, and **B14 built no route for it**, because B12
 * already did: `PATCH /cards/:id/title`, `cardTitlePatchSchema` in `./card`,
 * emitting `card.renamed`. Same column, same event, same validation, same
 * refusal of an empty title (`FR-CARD-002` AC3 and `FR-BOARD-022` AC3 are the
 * same sentence about the same string).
 *
 * The only difference between `FR-CARD-002` and `FR-BOARD-022` is **the surface
 * the edit came from** — the card back versus the quick edit on the board face
 * — and the server cannot see that difference, must not be told it, and would
 * have nothing to do with it if it were: `card.renamed`'s docstring in
 * `./events` says in as many words that the surface "is not something the feed
 * renders", so a second route would differ from the first only in its path. A
 * second body schema here would be `cardTitlePatchSchema` spelt twice, and the
 * two would drift the first time either the width or the trim rule changed.
 *
 * So this file declares no title primitive, no title patch and no title
 * constant, and `FR-CARD-002`'s server half is discharged by a route that
 * shipped one block earlier. What is genuinely `FR-CARD-002`'s and not
 * `FR-BOARD-022`'s is entirely on the client (`FE-14.2`): multi-line editing,
 * save on blur, revert on Escape, and AC4's read-only member getting no editor.
 *
 * ---------------------------------------------------------------------------
 * THIS IS NOT `BE-02.1`, AND IT IS NARROWER THAN `BE-02.1`'S CARD WOULD BE
 * ---------------------------------------------------------------------------
 * `BE-02.1` — the full Phase 1 object model (board, list, card, comment, label,
 * notification) — is a different item and stays PENDING. `./list`, `./card` and
 * `./board-contents` each recorded that about themselves, and it holds here with
 * the twist `./board-contents` already flagged for itself: **this file is the
 * one most likely to be mistaken for the object model landing**, because "the
 * card back owns the card's data" (the B14 block note) and a reader will expect
 * the card back's payload to be *the whole card*. It is not. It is
 * `cardSchema` plus **eleven** fields — a description, the card's list, the
 * card's members, (B15) its labels, (B24 stage 1) its start date, its due
 * date and its due-date reminder offset, (B24 stage 2) whether it is
 * complete, (B25 stage 1) its cover, (B26) its checklists, and (B27) its
 * attachments — because those are what B14's four routes, B15's two card
 * routes, B24's four date/completion routes, B26's eleven checklist routes,
 * B27's six attachment routes and `GET /cards/:id` itself read and write.
 * ⚠ **It said "three" through B14,
 * "four" through B23, "seven" through B24 stage 1, "eight" through B24
 * stage 2, "nine" through B25, "ten" through B26, and now "eleven" through
 * B27, and the count is corrected in place rather
 * than recounted each
 * time**, because the rule it illustrates is the load-bearing part: a field
 * arrives here **with the route that reads or writes it**, and `startAt`/
 * `dueAt`/`dueReminderMinutesBefore` arrived with
 * `PATCH /cards/:id/start-date`, `PATCH /cards/:id/due-date` and
 * `PATCH /cards/:id/due-date-reminder`, exactly as `labels` arrived with
 * `POST /cards/:id/labels` and `DELETE /cards/:id/labels/:labelId`, and
 * `completed` arrived with `PATCH /cards/:id/completed`. ⚠ **`cover` is the
 * one exception the rule has had to make room for**: no route *writes* one
 * yet (`BE-25.2`), so it arrives here with the route that *reads* it
 * instead, on `FR-BOARD-030`'s naming of `cover` as one of the four badge
 * fields the board face already needs — which made the card back's copy of
 * the same shape a one-field addition rather than a reason to wait.
 *
 * ⚠ **`checklists` is B26's, and it is neither `cover`'s exception nor
 * quite the ordinary case either — read which before adding a twelfth
 * field.** The rule above says a field arrives here *with the route that
 * reads or writes it*, and B26 ships eleven routes that write checklists
 * and their items. **Not one of them answers with this shape**: they answer
 * with `checklistSchema` and `checklistItemSchema` (`./checklist`), because
 * a checklist is addressed by its own id and a client that has just ticked
 * one item has no use for the whole card back. So the route this field
 * arrives with is `GET /cards/:id` itself — `cover`'s footing — while the
 * *reason* is the ordinary one: without it the card back cannot render a
 * card's checklists at all, and `FR-CARD-016`/`FR-CARD-017`/`FR-CARD-018`
 * are all card-back requirements. The narrower
 * `GET /cards/:id/checklists` exists beside it for re-reads after a
 * mutation (`FE-26.1`'s optimistic reconciliation) and is **not** a reason
 * to leave the array off the card back: a client opening a permalink would
 * otherwise pay a second round trip for a section the requirement puts on
 * the same screen.
 *
 * Everything else `BE-02.1` would put on a card is absent for the reason
 * `./card`'s header already enumerates, and each still has its own owner:
 * comments and the card's own activity `B16`/`B28` (`FR-COLLAB-*`).
 * ⚠ **`attachments` left that list at B27** — corrected in place rather
 * than deleted, on the identical rule `checklists` and `labels` below
 * illustrate: attachments were absent while `BE-27.x` was unbuilt, and they
 * arrive with the block that built them, gaining no per-viewer twist the
 * way `checklists` did (see the field's own docstring) and needing no
 * change to `readCardDetail`'s signature.
 * ⚠ **`checklists` left that list at B26**, exactly as
 * `labels` left it at B15 and on the identical rule — corrected in place
 * rather than deleted, because the way the list was right is the point:
 * checklists were absent while `BE-26.x` was unbuilt, and they arrive with
 * the block that built them.
 * ⚠ **`labels` left that list** — it was `BE-15.x`'s
 * (`FR-CARD-009`, `FR-CARD-010`) and `BE-15.x` has now built it, which is
 * exactly what this paragraph asked for; the arithmetic moved by one in each
 * direction and the rule did not move at all. A field added here "while we are
 * in the file" is a field with no route behind it, on the widest table in the
 * product, which is how `BE-02.1` quietly becomes unreviewable.
 *
 * ⚠ **"`completion and the dates are BE-24.x`" was true through B23, was
 * corrected at stage 1 to "half true" (the dates had landed, completion had
 * not), and is now wholly true again the other way — corrected a second
 * time rather than deleted, because the way each correction was wrong is the
 * useful half.** `BE-24.x` is a block run in stages: stage 1 (`BE-24.1`
 * `FR-CARD-013`, `BE-24.2` `FR-CARD-014`) built `PATCH /cards/:id/start-date`,
 * `PATCH /cards/:id/due-date` and `PATCH /cards/:id/due-date-reminder`, so
 * `startAt`, `dueAt` and `dueReminderMinutesBefore` arrived on
 * `cardDetailSchema` below with those three routes; stage 2 (`BE-24.4`,
 * `FR-CARD-004`) has now built `PATCH /cards/:id/completed`, so
 * **`completed` has arrived too**, on the identical rule — a field gains a
 * place on this shape only when the route that reads and writes it lands,
 * exactly as `labels` waited for `BE-15.x` and the three dates waited for
 * stage 1.
 *
 * ⚠ **`FR-CARD-007` AC4 names the card's activity section and this file carries
 * nothing of it.** "Given a user joins and then leaves, when the activity
 * section is expanded, then both events appear in the card's activity" is
 * satisfied on the *write* side by B14 — the four membership gestures each emit
 * a registered event, see `./events` — and on the *read* side by `B28`, which
 * owns the feed and the route that serves it. Two `activity_entries` rows
 * written by this block are what AC4 will read; an `activity` array on
 * `cardDetailSchema` would be `B28`'s surface built inside B14 against no
 * requirement of its own.
 *
 * ---------------------------------------------------------------------------
 * IT COMPOSES `./card` AND `./user` AND WIDENS NEITHER
 * ---------------------------------------------------------------------------
 * `cardDetailSchema` is `cardSchema.extend({ … })` **on a copy**, exactly as
 * `boardFaceListSchema` in `./board-contents` is `listSchema.extend({ … })` on a
 * copy, and for a sharper version of the same reason: `cardSchema` is the
 * response serialiser of all four B12 routes (`POST /cards`,
 * `GET /lists/:id/cards`, `PATCH /cards/:id/title`, `POST /cards/:id/move`)
 * **and** of `boardContentsSchema`'s `cards` array. Widening it would change
 * five shipped responses at once, and one of those five is the board face —
 * which would then ship every card's description, up to 16 KB each, on every
 * board open. `00-`'s third cross-cutting principle is that the board stays
 * legible at a glance; the payload staying small is the same principle on the
 * wire.
 *
 * `cardMemberSchema` extends `publicProfileSchema` for the reason
 * `boardMemberSchema` and `workspaceMemberSchema` both extend it: a member list
 * serves exactly "the portions of a profile another member may read"
 * (`FR-PERM-002`) and cannot acquire an `email` by accident.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE DELIBERATELY DOES NOT DECLARE
 * ---------------------------------------------------------------------------
 *   - **A result wrapper for the member add.** B10's
 *     `addBoardMemberResultSchema` is `{ added, member }`, and it exists because
 *     `FR-PERM-018`'s edge case demands it in as many words: "Adding a person
 *     who is already a member must be a no-op **with clear feedback**, not a
 *     duplicate entry" — and both outcomes are 200s, so the status code cannot
 *     carry the difference. Neither `FR-CARD-007` nor `FR-CARD-008` has that
 *     sentence: the members popover is a *toggle* whose current state the client
 *     is already rendering (AC3 — "when the user clicks them again in the
 *     popover, then they are removed from the card"), so the client knows which
 *     gesture it made. If a later block finds it needs the flag, it adds it with
 *     the requirement that asked for it.
 *
 *     ⚠ **This paragraph ended "`cardMemberSchema` below is the membership as it
 *     stands and is what there is to answer with; `DELETE` needs no shape at all,
 *     on `DELETE /boards/:id/members/:userId`'s precedent of answering `204` with
 *     no body." Corrected in place: BOTH member routes answer `200` with
 *     `cardDetailSchema`** — the whole card back, members array included. The
 *     paragraph was written as available-to-compose rather than as a mandate, and
 *     the routes composed differently; a header claiming 204 over a route
 *     answering 200 is exactly the drift this package documents against, so the
 *     claim is corrected rather than the reasoning deleted.
 *
 *     Why the routes went the other way, which does not disturb the argument
 *     above: B10's `DELETE /boards/:id/members/:userId` answers 204 because the
 *     caller **may be the person removed**, in which case a read-back through
 *     `requireBoardReader` would 404 on a private board they have just left and
 *     turn a successful departure into an error. A card carries no visibility of
 *     its own — leaving a card never costs the leaver their read of it — so that
 *     argument does not transfer, and the popover is a toggle whose next click is
 *     rendered from the state this response carries. `cardMemberSchema` is still
 *     "the membership as it stands"; it simply reaches the wire nested in
 *     `cardDetailSchema.members` rather than alone.
 *   - **Anything about description drafts.** `FR-CARD-003` AC2 — "given the user
 *     has typed unsaved text into the description, when they close the card back
 *     and reopen the same card, then the unsaved text is still present in the
 *     editor" — is real, is MVP, and is **not** served by any shape here.
 *     `card_description_drafts` (`db/postgres/migrations/0003_card_detail.sql`,
 *     one row per `(card_id, user_id)` holding `draft_text`) is a real table that
 *     B14 does not touch: the draft is per-viewer state, it has no event, and
 *     saving it on every keystroke is a write path with its own rate and its own
 *     conflict story. Named here so the next session does not re-derive the
 *     question and does not conclude the table was overlooked. `FE-14.2` may
 *     satisfy AC2 client-side (the account-scoped promise the column makes needs
 *     a route; the same-browser promise does not), and a block that wants the
 *     account-scoped version adds `PUT`/`DELETE /cards/:id/description/draft`
 *     with a shape of its own — it is not a nullable field on the patch body
 *     below, because a draft and a save are different intentions and a body that
 *     carried both would let a client save by accident.
 *   - **A `?state` or an archived branch.** There is none, for `./card`'s reason:
 *     nothing in this block archives a card (`FR-BOARD-028` is `BE-22.1`'s).
 *
 * The file follows the division every other feature file here follows —
 * **input primitives**, which validate and normalise what a route accepts, and
 * **views**, which describe what a route returns. The description is where that
 * division does the most work in this file: the input is capped and the view is
 * not, and the reason is spelt out at both ends.
 */

// ---------------------------------------------------------------------------
// Column widths — and the one number below that is NOT a column width.
//
// ⚠ `CARD_DESCRIPTION_MAX_LENGTH` IS DECLARED HERE RATHER THAN IMPORTED, AND
// UNLIKE EVERY OTHER CONSTANT IN THIS PACKAGE IT DOES NOT DESCRIBE A `varchar`.
//
// `./card`'s rule, stated when it imported `CARD_TITLE_MAX_LENGTH` from
// `./board` rather than restating a second 512: "import the constant that
// describes YOUR column if one exists, declare it if none does, and never share
// one across two columns." No constant in this package describes
// `cards.description`, so this is the one — and it must not be shared with
// `card_description_drafts.draft_text`, `boards.description` or
// `comments.body`, each of which is a separate column a separate requirement
// could cap separately.
//
// `cardListRefSchema.title` below is the other half of the same rule applied in
// the other direction: `LIST_TITLE_MAX_LENGTH` in `./list` already describes
// `lists.title`, so it is imported.
// ---------------------------------------------------------------------------

/**
 * The description cap — `FR-CARD-003`, "The description is capped at 16,384
 * characters", and AC4: "given a description of 16,384 characters, when the
 * user types an additional character, then the input is rejected and the limit
 * is communicated."
 *
 * ⚠ **This is a product rule, not a column width, and the difference is
 * load-bearing rather than pedantic.** `cards.description` is
 * `text NULL` — Postgres `text` is unbounded, and
 * `db/postgres/migrations/0003_card_detail.sql` records that the neighbouring
 * `card_description_drafts.draft_text` is `text` for the same reason. So unlike
 * `CARD_TITLE_MAX_LENGTH` or `LIST_TITLE_MAX_LENGTH`, exceeding this number is
 * **not** a `value too long for type character varying(n)` at INSERT time:
 * nothing in the database enforces it, and a row over the cap would be stored
 * happily.
 *
 * Two consequences, and they point in opposite directions on purpose:
 *
 *   - The **input** bound below is the only enforcement there is. If it is
 *     removed, AC4 is unimplementable server-side and the cap becomes a client
 *     courtesy a `curl` walks past.
 *   - The **view** (`cardDetailSchema.description`) is deliberately *not* bounded
 *     by it. See that field's docstring: a read that enforced a product cap
 *     would refuse to serve a row the database legitimately holds, which is the
 *     input/view split this package is arranged around.
 *
 * 16,384 = 2^14, and it is `FR-CARD-003`'s own number rather than a round figure
 * chosen here. Counted in **UTF-16 code units**, because that is what
 * `z.string().max()` measures and what a browser's `textarea.value.length`
 * reports — so the server and `FE-14.2`'s character counter agree without either
 * converting. An astral character (an emoji, most CJK extension B) therefore
 * costs two, which is a divergence from a byte count and from a Unicode
 * code-point count alike; the requirement says "characters" and does not
 * disambiguate, and agreeing with the editor that shows the counter is the
 * reading that makes AC4 observable.
 */
export const CARD_DESCRIPTION_MAX_LENGTH = 16384;

// ---------------------------------------------------------------------------
// INPUT primitives — what a route ACCEPTS.
// ---------------------------------------------------------------------------

/**
 * `PATCH /cards/:id/description` — `FR-CARD-003`. SC-4, field-scoped.
 *
 * The description moves on its own and can move nothing else — **never a
 * `PUT /cards/:id`**. `cardTitlePatchSchema` in `./card` gives the argument in
 * full and it applies here at its strongest, because this is the *second*
 * field-scoped patch on the widest table in the product and the two must not be
 * tempted into one body: a card back that saved title and description together
 * would make `FR-EXP-031`'s per-field last-write-wins inexpressible, and
 * `FR-COLLAB-026`'s promise — another member's edit to a different field must
 * not clobber the viewer's draft — is *the* thing `FR-CARD-003` AC2's draft is
 * about. (Phase 1 is last-write-wins **within** one field; the block note says
 * so and `B31` owns the collision handling. That is a known, accepted gap and
 * not a reason to widen the body.)
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE SCHEMA DOES NOT TRIM. THE ROUTE NORMALISES EMPTY-AFTER-TRIM TO `NULL`.
 * BOTH HALVES ARE DELIBERATE AND THEY ARE NOT THE SAME DECISION
 * ---------------------------------------------------------------------------
 * **Why no `.trim()` here**, when `boardTitleSchema`, `listTitleSchema`,
 * `cardTitleSchema`, `displayNameSchema` and `bioSchema` all trim: a description
 * is Markdown, and Markdown is whitespace-sensitive in ways a title is not.
 * Leading spaces inside a fenced code block are *content* — four of them are an
 * indented code block outright — a trailing blank line separates blocks, and two
 * trailing spaces are a hard line break in every CommonMark implementation. A
 * schema that trimmed would return a description the user did not write and,
 * worse, would do it silently: `FR-CARD-003` AC1's round trip ("type `## Plan`,
 * save, it renders as a heading") would still pass while a code block one line
 * further down lost its indentation.
 *
 * **Why the route still collapses the empty case.** `cards.description` is
 * nullable, so "this card has no description" could be spelt `NULL`, `''` or
 * `'   '` — three values for one state, and every reader in the product would
 * have to test for all three for ever. `FR-CARD-003` AC5 is the reader that
 * matters first: "given a card has a **non-empty** description, when the board
 * is viewed, then the card front shows a description indicator". That indicator
 * must be **one predicate**, `description IS NOT NULL`, computable in SQL
 * without a `trim()` in the `WHERE` clause on every card on the board.
 *
 * So the normalisation happens exactly once, in the route, on a value the schema
 * has already accepted verbatim: if the incoming string is empty after trimming,
 * the column is set to `NULL`; otherwise it is stored **untrimmed**, byte for
 * byte as sent. That is the only place in this package where a route normalises
 * something its schema deliberately did not, and it is documented at both ends
 * so neither end is "harmonised" into the other.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE FIELD IS NOT NULLABLE, WHICH IS THE OPPOSITE OF `bioPatchSchema`
 * ---------------------------------------------------------------------------
 * `./user` states the rule this looks like it breaks: "Clearing a bio is `null`,
 * never `''`. One state, one representation." Both files want the same
 * invariant — one representation in the column — and they enforce it at
 * different layers because their primitives differ:
 *
 *   - `bioSchema` **trims**, so the boundary can safely reject `''` and demand
 *     `null`; `'   '` and `''` are the same request there, and refusing both is
 *     free.
 *   - A description must **not** be trimmed, so the boundary cannot tell `''`
 *     from `'   '` without rewriting Markdown — and refusing whitespace-only
 *     text would refuse a legitimate save from an editor that emitted a stray
 *     newline, which is a 422 on the Save button for a card the user was trying
 *     to clear.
 *
 * Accepting `null` **as well** was the third option and is worse than either:
 * it would give the caller two spellings of one intention (`null` and `''`) for
 * a request the route already understands in one, and a client that sent `null`
 * on Cancel and `''` on Save-empty would look like it was doing two different
 * things. One spelling in, one state stored.
 */
export const cardDescriptionPatchSchema = z.strictObject({
  description: z
    .string()
    .max(CARD_DESCRIPTION_MAX_LENGTH)
    .describe(
      'The card description as Markdown, up to 16,384 characters (FR-CARD-003). ' +
        'Sent verbatim: leading and trailing whitespace is NOT stripped, because ' +
        'Markdown is whitespace-sensitive. Send an empty (or whitespace-only) ' +
        'string to clear the description — the server stores NULL for it, so ' +
        '“this card has no description” is one state and the card front’s ' +
        'indicator is one predicate (AC5). null is not accepted: it would be a ' +
        'second spelling of the same request.',
    ),
});

export type CardDescriptionPatch = z.infer<typeof cardDescriptionPatchSchema>;

/**
 * `POST /cards/:id/members` — `FR-CARD-007` (Join, when the id is the caller's
 * own) and `FR-CARD-008` (assign somebody else, when it is not).
 *
 * **One body, one field, and it is emphatically not B10's shape.**
 * `addBoardMemberBodySchema` in `./board` is a union of `{ email }` and
 * `{ username }`, because `FR-PERM-018` describes adding somebody to a board "by
 * email address or by username" — a person who may not be in front of the
 * inviter at all, and who under `FR-PERM-019`/`FR-PERM-020` might not even have
 * an account yet.
 *
 * A card is the opposite situation and the requirement says so: `FR-CARD-008`'s
 * behaviour text is "The members popover **lists board members** with a search
 * box and toggles membership on click … **Only users who are members of the
 * card's board can be added; adding a non-board member is not possible from this
 * popover**", and AC4 makes the exclusion observable ("given a person who is not
 * a board member, when the user searches for them in the members popover, then
 * they do not appear as a selectable option"). Every candidate is therefore a
 * row the client has already read from `GET /boards/:id/members` — it holds
 * their `id` — so an id is what it sends.
 *
 * Offering an email path here would be worse than redundant: it would be the
 * one shape in this file capable of naming somebody **not on the board**, which
 * is precisely what `FR-CARD-008` closes, and the route would have to refuse it
 * after resolving an address — turning the card popover into an existence oracle
 * for addresses in the product. A `ulidSchema` cannot ask that question.
 *
 * ⚠ **There is no `role`, and there is no card-level role to give.** A card
 * carries membership and nothing else; see `cardMemberSchema`.
 *
 * ⚠ **OBSERVERS ARE NOT REFUSED, HERE OR ANYWHERE IN B14, AND THAT IS
 * DELIBERATE.** `FR-CARD-007`'s edge case says "Observers and read-only members
 * cannot join cards" — that is `FR-PERM-016` restated, it is **V1 rather than
 * MVP**, and its content-write exclusions are a bundle `BE-38.1` must land
 * together. `requireBoardWriter` still admits observers today and `CLAUDE.md`
 * says in as many words: "Do not 'fix' one to match the other." So this schema
 * carries no observer concept, the route guarding it is `requireCardWriter`
 * unchanged, and **adding a refusal in this block would be a defect rather than
 * a feature** — one exclusion enforced while the neighbouring ones are not is
 * how a permission model becomes untestable. Recorded here because this
 * docstring is where a route author looks before writing the guard.
 *
 * ⚠ **The body is the same for Join and for assigning somebody else, and the
 * server tells them apart by comparing `userId` against the session** — that is
 * what selects `member.self_joined` over `member.added` (see `./events`). There
 * is deliberately no separate `POST /cards/:id/join`: it would be this route
 * with the id filled in from the session, two paths writing one row, and the
 * "am I adding myself?" comparison would still have to exist on the other one.
 */
export const addCardMemberBodySchema = z.strictObject({
  userId: ulidSchema.describe(
    'The account to put on the card. Must already hold a board_members row on ' +
      'the card’s board (FR-CARD-008) — a workspace admin who has never been ' +
      'added to the board is not addable, the same no-cascade reading every ' +
      'board-role guard takes. Send the caller’s own id for FR-CARD-007’s Join; ' +
      'the server chooses the event by comparing this against the session. ' +
      'Adding somebody already on the card is a legal no-op and records nothing.',
  ),
});

export type AddCardMemberBody = z.infer<typeof addCardMemberBodySchema>;

// ---------------------------------------------------------------------------
// Views — what a route RETURNS.
//
// Width-bounded strings, no format checks, no transforms — see the note above
// the input primitives, and `./user`'s longer version of the same argument.
// The description is the one field where "width-bounded" is wrong; its
// docstring says why.
// ---------------------------------------------------------------------------

/**
 * One row of a card's member list — `FR-CARD-007` AC1 ("their avatar appears in
 * the card's members section and on the card front") and `FR-CARD-008` AC2.
 *
 * `publicProfileSchema` plus one stamp, which makes it **structurally the same
 * shape as `boardMemberSchema` and `workspaceMemberSchema` minus their `role`**
 * — and the missing role is the entire reason this is a third declaration
 * rather than a reuse of either.
 *
 * ⚠ **Corrected in place at B38: it is now that shape minus their `role` AND
 * minus `boardMemberSchema`'s `isGuest`.** `FR-PERM-023`'s edge case put a
 * derived `isGuest` on the *board* member view alone (a board member who holds
 * no row in the board's workspace), and it is absent here for a reason one step
 * beyond the `role` argument below rather than an oversight: a card confers no
 * capability, so "is this person external to the workspace" is not a fact a
 * card's member list acts on, and a **second** derived copy of a two-table join
 * would go stale against the board's member list the instant somebody joined
 * the workspace. Where the `role` argument says *the authority lives on
 * `board_members`*, this one says *the derivation lives where the requirement
 * asked for it*. Same conclusion, same place to read it: `GET /boards/:id/
 * members`, which a client rendering this popover already holds.
 *
 * ---------------------------------------------------------------------------
 * ⚠ WHY THERE IS NO `role` HERE, AND WHY ADDING ONE WOULD BE A SECOND SOURCE OF
 * TRUTH RATHER THAN A CONVENIENCE
 * ---------------------------------------------------------------------------
 * `card_members` is `(card_id, user_id, joined_at)` and nothing else — there is
 * no `card_members.role` column and no CHECK constraint to mirror, because a
 * card confers no capability on the people on it. `FR-CARD-007`'s own edge case
 * says it plainly: "A card may have zero members; **there is no implicit
 * assignee**." Membership of a card is a signal ("I am working on this", "you
 * are needed here") and a notification subscription, not an authority.
 *
 * What *does* carry a role is `board_members`, and a `role` field here would be
 * that fact copied onto a second row. It would be a **stale** copy the first
 * time a board admin changed somebody's board role while their avatar sat on
 * forty cards, and nothing would fail — the copy would simply disagree with the
 * board's member list, on a field no requirement asked for. `boardMemberSchema`
 * is where a board role belongs and `GET /boards/:id/members` is what serves it.
 *
 * A client that wants to grey out an observer's avatar reads the board's member
 * list, which it already has — the same list `FR-CARD-008`'s popover is built
 * from.
 */
export const cardMemberSchema = publicProfileSchema.extend({
  /**
   * `card_members.joined_at timestamptz(3) NOT NULL DEFAULT now()`.
   *
   * A bounded string rather than `z.iso.datetime()` or a coerced `Date`, for the
   * reason `workspaceMemberSchema.joinedAt` states at length and
   * `boardMemberSchema.joinedAt` inherits: `pg` hands a `timestamptz` back in
   * Postgres' own spelling (`2026-08-11 12:00:00.123+00`), which is a legitimate
   * instant and not an ISO 8601 `T`/`Z` form, so pinning the serialisation here
   * would make this schema reject the value the database actually produced.
   *
   * ⚠ One small difference from `boardMemberSchema.joinedAt` worth recording,
   * because it looks like an inconsistency and is the opposite of one: **there
   * the field renames its column** (`board_members.created_at` → `joinedAt`,
   * "a fact about the person, not about the row"), whereas here the column is
   * *already* called `joined_at`. `0003_card_detail.sql` named it that when the
   * schema was written; the wire name and the column name agree by luck rather
   * than by translation, and the field is named for the same reason regardless.
   */
  joinedAt: z.string().min(1),
});

export type CardMember = z.infer<typeof cardMemberSchema>;

/**
 * The card's list, as the card back names it — `FR-CARD-001`.
 *
 * Three fields: which list, what it is called, and whether it is archived. The
 * card back renders **"in list X"** beside the title; it does not render a list,
 * and this shape must not grow towards `listSchema`.
 *
 * ⚠ **It is NOT `listSchema` and must never become it.** `listSchema` is the
 * response serialiser of six of B11's seven routes and carries `position`,
 * `cardCount`, `createdAt` and `updatedAt` — a rank the card back cannot use, a
 * count it does not show, and two timestamps `listSchema`'s own docstring
 * describes as "the exception rather than the pattern". Nesting the whole list
 * view here would put all four on every card open and make a future widening of
 * `listSchema` (a colour, `B58`) silently change this response too. Compare
 * `./board-contents`, which nests `boardSchema` **whole** for the opposite,
 * equally deliberate reason: a client opening the board face needs the board's
 * header facts and would otherwise fetch them separately, whereas a client
 * opening a card back has just come *from* the board and needs only the list's
 * name.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `archived` IS THE WHOLE REASON THIS SHAPE EXISTS, AND IT IS THE REASON
 * `GET /cards/:id` OMITS A CONJUNCT THAT THREE OTHER CARD READS CARRY. READ
 * THIS BEFORE "HARMONISING" ANY OF THE FOUR
 * ---------------------------------------------------------------------------
 * `FR-CARD-001`'s first edge case: "**A card whose list has been archived is
 * still reachable by permalink; the card back indicates the card is in an
 * archived list.**" Two clauses, and this field is the second one.
 *
 * The first clause is what makes `GET /cards/:id` different in kind from every
 * card read shipped so far. `GET /lists/:id/cards` (B12) and
 * `GET /boards/:id/contents` (B13) both filter on the parent list's
 * `archived_at IS NULL`, and B13's note calls its copy of that line "the line
 * most likely to be broken by a future refactor". **This read must not carry
 * it**, and the two positions are not in tension — they are the same
 * requirement seen from two surfaces:
 *
 *   - A **board-face** read answers "what is on the board", and `FR-VIEW-007`'s
 *     edge case removes an archived list and its cards from it. A card in an
 *     archived list is correctly *absent* there.
 *   - A **permalink** read answers "what is this card", and `FR-CARD-001` says
 *     it still resolves. A card in an archived list is correctly *present* here,
 *     with the fact surfaced rather than filtered.
 *
 * `./list`'s docstring on `listSchema.archived` itemised the bill B11 left for
 * the card reads and named its debtors: "the card read paths (`BE-12.x`,
 * `BE-13.1`) must filter on the parent list's `archived_at IS NULL`". **`BE-14.1`
 * was never on that list**, and this is why. Both named items paid it; this one
 * was never billed.
 *
 * The mechanism that makes the read possible is already in place and was
 * measured rather than assumed: `isLiveList()` in
 * `apps/server/src/lists/permissions.ts` is `isNull(lists.deletedAt)` **only**,
 * so `requireCardWriter` already admits a card in an archived list and the read
 * guard does too. Nothing had to be relaxed for `FR-CARD-001`; what had to be
 * added is this field, so that the client can say so.
 *
 * ⚠ The failure mode if this field is ever dropped is **silent**, which is the
 * house reason for the length of this note: the route would keep returning the
 * card, the client would keep rendering it, and the only symptom would be a card
 * back that looks like a card on a live board while its list is in the archive —
 * no status code, no missing field, nothing red. `cardSchema.archived` cannot
 * stand in for it: that boolean is the **card's own** flag and a card in an
 * archived list legitimately reads `archived: false`, which is true of the card
 * and wrong for the board. `./card`'s and `./list`'s docstrings both say so from
 * their side; this is the shape that finally puts the list's flag on the wire.
 */
export const cardListRefSchema = z.object({
  id: ulidSchema,
  /**
   * `lists.title varchar(512)` — bounded by `LIST_TITLE_MAX_LENGTH` from
   * `./list`, imported rather than restated because that constant already
   * describes this column. See the note above the constants.
   */
  title: z.string().max(LIST_TITLE_MAX_LENGTH),
  /**
   * `lists.archived_at IS NOT NULL` — `FR-BOARD-015`, and `FR-CARD-001`'s edge
   * case. See the shape's docstring: this boolean is the whole point of it.
   *
   * `false` on every card reached from the board face, and `true` only on a card
   * reached by permalink after its list was archived — which is exactly the
   * state the card back is required to indicate.
   */
  archived: z.boolean(),
});

export type CardListRef = z.infer<typeof cardListRefSchema>;

/**
 * `GET /cards/:id` — the card back in one response. `FR-CARD-001`, and the
 * shape `PATCH /cards/:id/description` answers with once it has written.
 *
 * `cardSchema.extend({ description, list, members, labels, startAt, dueAt,
 * dueReminderMinutesBefore, completed, cover, checklists })` — **on a copy**,
 * ten fields added and not one of `cardSchema`'s eleven restated. (Three when
 * B14 shipped it; `labels` is B15's, `FR-CARD-009`; `startAt`/`dueAt`/
 * `dueReminderMinutesBefore` are B24 stage 1's, `FR-CARD-013`/`FR-CARD-014`;
 * `completed` is B24 stage 2's, `FR-CARD-004`; `cover` is B25 stage 1's,
 * `FR-CARD-021`; `checklists` is B26's, `FR-CARD-016`–`FR-CARD-019` — and
 * the header records why each arrived here rather than
 * on `cardSchema` as this file once predicted.)
 *
 * ---------------------------------------------------------------------------
 * ⚠ `cardSchema` ITSELF IS NOT WIDENED, AND FIVE SHIPPED RESPONSES ARE WHY
 * ---------------------------------------------------------------------------
 * `boardFaceListSchema` in `./board-contents` records the same warning for
 * `listSchema` and this one is sharper, because `cardSchema` serialises **five**
 * shipped surfaces rather than six routes in one block: `POST /cards`,
 * `GET /lists/:id/cards`, `PATCH /cards/:id/title` and `POST /cards/:id/move`
 * (B12), plus every element of `boardContentsSchema`'s `cards` array (B13).
 * Adding `description` to `cardSchema` would change all five by a byte at
 * minimum and by 16 KB per card at maximum — on the board face, which is the one
 * response in the product that scales with the size of the board.
 *
 * That is `00-`'s third cross-cutting principle expressed on the wire: the board
 * must stay readable in ten seconds, and a board read that carries every card's
 * full description is the same feature working against the same goal one layer
 * down. The card back is opened one card at a time; the board face is opened
 * with two hundred.
 *
 * The dividend of extending rather than restating is `./board-contents`' and is
 * inherited whole: there is **one** `cardSchema` in this package, so a client
 * that has dragged a card on the board face and a client that has opened its
 * back are looking at the same eleven fields, and a later block that widens
 * `cardSchema` (labels, `BE-15.x`; dates, `BE-24.x`) reaches this response for
 * free without touching this file.
 *
 * ⚠ **THE LAST CLAUSE NAMED `BE-15.x` AND `BE-15.x` DID THE OPPOSITE.
 * CORRECTED IN PLACE.** B15 (labels) is the first of the two blocks that
 * sentence predicts, and it did **not** widen `cardSchema`: it declared
 * `cardLabelSchema` in `./label` and **this file was touched**, gaining a
 * fourth extended field (`labels`, below) exactly as `./board-contents` gained
 * `boardFaceCardSchema`. The "for free without touching this file" mechanism is
 * real and still available; it simply was not used, because a label belongs on
 * the two surfaces that render a card in context and on none of the four B12
 * routes that answer "what is this card now" — `POST /cards/:id/move` would
 * otherwise acquire a label read on the drag path.
 *
 * The dividend sentence above is unaffected and worth keeping: there is still
 * one `cardSchema`, the eleven fields are still identical across every surface,
 * and what B15 changed is which shapes carry a twelfth. ⚠ **B24 stage 1 weighed
 * the same trade and went the same way as B15, not as the "for free" prediction
 * above.** `startAt` and `dueAt` are wanted on the board face too
 * (`FR-CARD-013`'s badge on the card front) and are added to
 * `boardFaceCardSchema` in `./board-contents` on a copy of their own, rather
 * than by widening `cardSchema` and letting both surfaces inherit it — the same
 * two-surfaces-not-four argument B15's correction made, with
 * `dueReminderMinutesBefore` staying off the board face entirely because no
 * requirement puts the reminder offset on a card front. A later badge block
 * (the cover, `BE-25.x`) should weigh the trade again rather than inherit
 * either answer — and note the cost, which `./board-contents` states in full:
 * the compile-time net that turns "a field added to `cardSchema`" into a
 * compile error in every local mapper does not fire for a field added only to
 * a derived shape. Here it re-arms itself, because `toCardDetail`'s return type
 * is already `CardDetail`; on the board face it had to be re-armed by hand.
 *
 * ---------------------------------------------------------------------------
 * WHAT A CLOSED BOARD AND AN ARCHIVED LIST DO TO THIS SHAPE: NOTHING
 * ---------------------------------------------------------------------------
 * Neither is expressible here and neither should be. A **closed** board answers
 * `200` with the card — `FR-EXP-012` AC2 wants a read-only page rather than a
 * not-found, which is the call `GET /boards/:id/contents` already made, and the
 * board's `closed` flag is on `boardSchema`, which this shape does not nest (the
 * client opening a card back came from the board and holds it). An **archived
 * list** answers `200` with `list.archived: true` — see `cardListRefSchema`.
 * A caller outside the board's read scope gets `404` and no shape at all,
 * because answering `401` or `403` would make the route an existence oracle;
 * `FR-CARD-001` AC4's "permission message" is the client's rendering of that
 * `404`.
 */
export const cardDetailSchema = cardSchema.extend({
  ...cardAgingFieldsSchema.pick({ agingExempt: true, lastActivityAt: true }).shape,
  /**
   * `cards.description text NULL` — `FR-CARD-003`. `null` means the card has no
   * description, and it is the **only** value that means that: the route
   * collapses an empty-after-trim submission to `NULL` (see
   * `cardDescriptionPatchSchema`), so no card ever holds `''`, and AC5's card-front
   * indicator is the single predicate `description IS NOT NULL`.
   *
   * ⚠ **Deliberately NOT bounded by `CARD_DESCRIPTION_MAX_LENGTH`, though the
   * patch body is.** This is the input/view split at its sharpest and the most
   * likely thing in this file to be "fixed" for symmetry. The cap is
   * `FR-CARD-003`'s product rule; the column is unbounded `text` and carries no
   * CHECK, so a longer value is storable — by a fixture, a seed, an import path
   * (`FR-EXP-005`, unowned), or by any row written before this route existed. A
   * view that enforced the cap would make `GET /cards/:id` **500 on a row the
   * database legitimately holds**, and it would do so only for the users whose
   * data provoked it. Reject on the way in, render what is there on the way out
   * — `publicProfileSchema.bio` and `avatarUrl` are the same call for the same
   * reason.
   *
   * ⚠ **Deliberately NOT on `cardSchema`.** See the shape's docstring: the board
   * face must not ship a description per card.
   *
   * The value is Markdown source, served **verbatim** — not rendered, not
   * sanitised, not trimmed. Rendering is `FE-14.2`'s (`FR-CARD-003`'s editor
   * and its formatting toolbar are both client concerns — the requirement's
   * WYSIWYG mandate and its raw-markdown preference were struck 2026-09-06),
   * and a server that
   * returned HTML here would be choosing the renderer for every future client
   * and would have to re-sanitise on every read.
   */
  description: z.string().nullable(),
  location: cardLocationSchema.nullable(),
  /**
   * The list this card is in — id, title and archived flag.
   *
   * ⚠ **This does not duplicate `cardSchema.listId`, and both are on the wire on
   * purpose.** `listId` is the id a client compares against the list it dragged
   * from and the key `POST /cards/:id/move` is answered in terms of; `list` is
   * the *rendering* of "in list Doing" at the top of the card back, plus the one
   * fact no id can carry — whether that list is archived. Serving only the id
   * would put a second request (`GET /boards/:id/lists`) on the critical path of
   * every permalink open, on a board the caller may not otherwise have loaded.
   * `list.id` and `listId` always agree; the route reads both from the same row.
   */
  list: cardListRefSchema,
  /**
   * The people on this card — `FR-CARD-007`, `FR-CARD-008`.
   *
   * ⚠ **Empty is the ordinary state, not a degenerate one.** `FR-CARD-007`'s
   * edge case: "A card may have zero members; **there is no implicit assignee**."
   * Every card `POST /cards` creates arrives with `members: []`, the array is
   * never omitted, and a client must not read absence and emptiness as the same
   * thing — only one of them can happen.
   *
   * Ordered by the server. `FR-CARD-007` and `FR-CARD-008` specify no order for
   * the avatar row, so the route picks a stable one and states it there rather
   * than leaving the planner to return `card_members` in whatever order it likes
   * — an avatar row that reshuffles between two reads of an unchanged card is a
   * bug with no error attached, and it is the `ORDER BY position, id` lesson
   * from `./rank` applied to a table that has no rank.
   */
  members: z.array(cardMemberSchema),
  /**
   * The labels on this card — `FR-CARD-009`, whose behaviour text is "Coloured
   * labels render on **both card front and card back**".
   *
   * `cardLabelSchema` from `./label`: `{ id, name, colour }`, the identical
   * element `boardFaceCardSchema.labels` carries. **One shape for both
   * surfaces** is the point — a card front and a card back that described a
   * label differently would be `./board-contents`' second-card-view failure one
   * level down, and it is worse here, because the two arrays are meant to be
   * comparable element for element.
   *
   * ⚠ **Ordered by the BOARD'S LABEL SET, not by application time**, and
   * identically to the board face. `FR-CARD-009` AC2 fixes the order for the
   * card front ("in the board's label set order") and says nothing about the
   * card back — but the two must not diverge, because a member who applies
   * three labels and then opens the card is comparing them by eye. The join is
   * `card_labels → labels` with `ORDER BY labels.position, labels.id` on both
   * routes; sorting by `card_labels.created_at` passes every "the three labels
   * are there" assertion and fails the only sentence that says what order means
   * here.
   *
   * ⚠ **Colourless labels are present here AND on the board face.** AC4 —
   * "given a colourless label is applied, when the board is viewed, then it
   * appears on the card back but not on the card front" — is a **rendering**
   * rule `FE-15.2` owns, and the card back is the surface it appears on
   * regardless. Filtering it out of the board face server-side would leave the
   * two routes returning different arrays for one card with no status code to
   * say so, and would break `FR-CARD-012`/`FR-SEARCH-003` by construction. This
   * is `cardListRefSchema.archived`'s lesson exactly: **project the fact,
   * filter nothing.**
   *
   * ⚠ **Empty is the ordinary state.** A card carries "any number of them"
   * including none, boards created before B15 have no labels at all to carry
   * (there is no backfill), and the array is never omitted — a client must not
   * read absence and emptiness as the same thing, because only one of them can
   * happen.
   *
   * ⚠ **Deliberately NOT on `cardSchema`.** See the shape's docstring and the
   * header: B12's four routes serve no labels and perform no label read.
   */
  labels: z.array(cardLabelSchema),
  /**
   * `cards.start_at timestamptz(3) NULL` — `FR-CARD-013`, written by
   * `PATCH /cards/:id/start-date`. `null` means the card has no start date.
   *
   * ⚠ **A bounded-below string and emphatically NOT `z.iso.datetime()`** —
   * `comments.ts`'s `editedAt` and every other timestamp view in this package
   * make the identical call: `pg` hands a `timestamptz` back in Postgres' own
   * spelling (`2026-08-12 12:00:00.123+00`, a space rather than a `T`, no
   * trailing `Z`), which is a legitimate instant and not an ISO 8601 form.
   * `z.iso.datetime()` here would make this the only view in the package to
   * reject the value the database actually produced — a 500 on every card
   * back for any card that has ever had a start date.
   *
   * ⚠ **This is the opposite asymmetry from the INPUT side.**
   * `cardStartDatePatchSchema.startAt` in `./card-dates` validates a strict
   * ISO-8601 instant and refuses Postgres' own spelling; this field does the
   * reverse. That is not an inconsistency to "fix" — the two schemas guard two
   * different directions of the same value, on `cardSchema.createdAt`'s
   * house rule for a `timestamptz` read back through `pg`.
   */
  startAt: z.string().min(1).nullable(),
  /**
   * `cards.due_at timestamptz(3) NULL` — `FR-CARD-013`, written by
   * `PATCH /cards/:id/due-date`. `null` means the card has no due date.
   *
   * Same serialisation note as `startAt` above: Postgres' own spelling,
   * never `z.iso.datetime()`.
   */
  dueAt: z.string().min(1).nullable(),
  /**
   * `cards.due_all_day boolean NOT NULL DEFAULT false` — `FR-EXP-029` edge
   * case (BE-44.7). `true` means `dueAt` (midnight-UTC-normalised) is a
   * date-only value rendered as the same calendar date for every viewer;
   * `false` keeps instant semantics. Not nullable: every card has an answer.
   */
  dueAllDay: z.boolean(),
  /**
   * `cards.due_reminder_minutes_before integer NULL`, constrained by
   * `ck_cards_due_reminder` to `DUE_REMINDER_OFFSETS` (`./card-dates`) or
   * `NULL` — `FR-CARD-014`, written by `PATCH /cards/:id/due-date-reminder`
   * and defaulted by `PATCH /cards/:id/due-date` on a first-time due-date set
   * (AC1's 24-hour default).
   *
   * ⚠ **Parsed through `dueReminderOffsetSchema`, never cast** —
   * `labelColourSchema.parse`'s house rule for a value read out of a column
   * with a CHECK behind it, applied to the first *integer* column in this
   * package that carries one. A value that fails to parse means
   * `DUE_REMINDER_OFFSETS` and `ck_cards_due_reminder` have drifted, which a
   * read should surface rather than silently pass a ninth number through.
   *
   * ⚠ **`null` here can mean two different things the schema cannot tell
   * apart, and neither route needs it to.** Either the card has no due date at
   * all (in which case this is `null` because `dueAt` is too — the orphan
   * state `due_at IS NULL AND due_reminder_minutes_before IS NOT NULL` has no
   * CHECK, no FK and no trigger behind it, and the two mutation routes are the
   * whole of what keeps a card out of it), or the card has a due date and the
   * reminder is explicitly set to **None** (`FR-CARD-014`'s own option). A
   * client that wants to distinguish the two reads this field alongside
   * `dueAt`, which is already on the wire beside it.
   *
   * ⚠ **Deliberately NOT on `boardFaceCardSchema`.** `./board-contents`'s copy
   * of this shape carries `startAt` and `dueAt` (`FR-CARD-013`'s card-front
   * badge names both explicitly) but not this field — nothing in the corpus
   * puts a reminder offset on the board face, and the board face is the one
   * response in the product that scales with the size of the board.
   */
  dueReminderMinutesBefore: dueReminderOffsetSchema.nullable(),
  /**
   * `cards.completed boolean NOT NULL DEFAULT false` — `FR-CARD-004`, B24
   * stage 2, written by `PATCH /cards/:id/completed`.
   *
   * ⚠ **Independent of every date field beside it.** `FR-CARD-004`'s own
   * behaviour text: "Completion is independent of due dates — a card with no
   * due date can be marked complete." So `completed: true` with `dueAt:
   * null` is a legal and ordinary combination, not a state a client should
   * treat as inconsistent; the two toggles are read and written by two
   * different routes and neither constrains the other.
   *
   * Not nullable — `cards.completed` carries `NOT NULL DEFAULT false`, so
   * every card this shape ever describes has an answer, unlike `startAt` /
   * `dueAt` / `dueReminderMinutesBefore`, which are genuinely absent on most
   * cards. There is no third state to represent.
   *
   * ⚠ **Deliberately ON `boardFaceCardSchema` too, unlike
   * `dueReminderMinutesBefore` just above.** `FR-CARD-004`'s own behaviour
   * text names a card-front control ("a completion toggle … appears on
   * hover on the card front") and AC1 requires the green check to show "on
   * both card front and card back" — the identical footing `startAt`/
   * `dueAt` are on in `./board-contents`, and the opposite footing
   * `dueReminderMinutesBefore` is on (nothing in the corpus puts a reminder
   * offset on a card front). See `boardFaceCardSchema`'s own docstring for
   * the field.
   */
  completed: z.boolean(),
  /**
   * This card's cover, or `null` for no cover — `FR-CARD-021`, B25 stage 1,
   * `FE-25.2`'s popover state.
   *
   * `null` for every card in the product today: `GET /cards/:id` is a read
   * and nothing yet writes a cover (`BE-25.2`, `PUT`/`DELETE
   * /cards/:id/cover`). Added now rather than deferred, on the same
   * reasoning `boardFaceCardSchema.cover` (`./board-contents`) states: the
   * shape already exists because the board face needs it, and a permalink
   * that disagreed with the board face about whether a card had a cover —
   * even while both answers are `null` — would be exactly the kind of
   * two-card-views drift this package is arranged to make impossible.
   *
   * ⚠ **Deliberately NOT `hasDescription` or `commentCount`, which stop at
   * `boardFaceCardSchema`.** `hasDescription` is redundant with this shape's
   * own `description` field — a client already holding the real text
   * computes presence trivially, and a derived boolean beside its own
   * ground truth is the second-source-of-truth failure `cardMemberSchema`'s
   * missing `role` already refuses one field up. `commentCount` has nowhere
   * honest to come from here either: `GET /cards/:id/comments` is a keyset
   * stream that deliberately exposes no total (`FR-COLLAB-001`'s shape), so
   * a count on the card back would be the one number in this response with
   * no query behind it. A badge is a summary that exists *because* the card
   * back is not open — `FR-CARD-034` AC4 only requires it "when the board is
   * viewed."
   */
  cover: cardCoverSchema.nullable(),
  /**
   * This card's checklists, in card order (`ORDER BY position, id`), each
   * carrying **all** of its items in rank order — `FR-CARD-016`,
   * `FR-CARD-017`, `FR-CARD-018`, B26.
   *
   * `checklistSchema` from `./checklist`, nested **whole** and narrowed by
   * nothing. That is the opposite call from `cardListRefSchema` two fields
   * up and from `cardLabelSchema`, both of which narrow what they nest, and
   * the reason is the surface rather than the shape: a card back **is** the
   * checklist section, so every field `checklistSchema` carries — the
   * title, the rank, the per-caller `hideChecked`, the items — is rendered
   * here, and a narrowed copy would have to be widened back the first time
   * `FE-26.1` needed one of them. The board face, which genuinely cannot
   * afford the array, carries two integers instead
   * (`boardFaceCardSchema.checklistItemCount` /
   * `.checklistCheckedItemCount`, `./board-contents`) and nests nothing.
   *
   * ⚠⚠ **This is the first field on this shape whose VALUE DEPENDS ON WHO
   * ASKED, and the whole response stops being cacheable across accounts
   * because of it.** `checklistSchema.hideChecked` is read from
   * `checklist_hide_checked_prefs` for the calling account alone
   * (`FR-CARD-018`'s edge case: the preference is per user per checklist
   * and "must not change what other members see"), so two members opening
   * one card legitimately receive different bytes. The consequence for the
   * server is stated at `readChecklists` and is the silent failure of this
   * whole stage: a caller that reads the card back **without** passing the
   * session's account gets `hideChecked: false` everywhere, which is a
   * valid, plausible, fully-populated response in which one member's
   * toggle simply never takes effect.
   *
   * ⚠ **The array is COMPLETE — checked items are never filtered out**,
   * whatever `hideChecked` says. That flag is a rendering instruction
   * `FE-26.2` obeys; `FR-CARD-018` requires the toggle to indicate *how
   * many* items are hidden and requires a `checked/total` counter beside
   * it, and both numbers can only be computed from items the client was
   * actually sent. This is `cardListRefSchema.archived`'s rule again:
   * project the fact, filter nothing.
   *
   * ⚠ **No `checklistItemCount` or `checkedItemCount` rides beside it**, on
   * either the checklist or this shape. They are `items`' own
   * (`items.length` and `items.filter((i) => i.checked).length`), and
   * `./checklist`'s header argues at length why shipping them would make
   * disagreement between a count and its own array representable. The
   * board face's two integers are a different thing entirely — there the
   * items are genuinely absent, so the counts are the only source there is.
   *
   * ⚠ **Empty is the ordinary state**, and more so than for `labels` or
   * `members`: every card in the product carries `checklists: []` until
   * somebody adds one, and there is no seed and no backfill. The array is
   * never omitted, so a client must not read absence and emptiness as the
   * same thing — only one of them can happen.
   *
   * ⚠ **Deliberately NOT on `cardSchema` and NOT on `boardFaceCardSchema`.**
   * See the shape's docstring: this is the one response in the product that
   * scales with the size of the board, and a board face carrying every
   * card's every checklist item would be `description`'s 16 KB problem with
   * no upper bound at all.
   */
  checklists: z.array(checklistSchema),
  /**
   * This card's attachments, most-recent-first — `FR-CARD-022`, `FR-CARD-023`,
   * `FR-CARD-024`, B27.
   *
   * `attachmentListSchema` from `./attachment`, nested **whole** and narrowed
   * by nothing — `checklists`' precedent immediately above, not
   * `cardListRefSchema`'s or `cardLabelSchema`'s: `FR-CARD-024`'s
   * next/previous preview navigation needs the WHOLE set client-side, which a
   * paged or narrowed copy cannot serve at a page edge, so every field
   * `attachmentSchema` carries is rendered here.
   *
   * ⚠ **This field has NO per-viewer variant, and that is what let it land on
   * `readCardDetail(handle, cardId)` with no signature change** — unlike
   * `checklists` immediately above, which forced a THIRD parameter
   * (`userId`) onto that function at B26 because `hideChecked` is read for
   * the calling account. An attachment renders identically for every caller
   * who may read the card at all, `members`'/`labels`' footing rather than
   * `checklists`'. `RelatedCard` (`./attachment`, `BE-27.5`) is the
   * contrasting case that DOES need a viewer and is deliberately NOT on this
   * shape — see `GET /cards/:id/relations`'s own module header for why
   * related cards ride a route of their own rather than force `userId`
   * through this file's other eight callers.
   *
   * ⚠ **Deliberately NOT on `boardFaceCardSchema` or `cardSchema`.**
   * `boardFaceCardSchema.attachmentCount` (`./board-contents`) carries the
   * one number the board face can afford; the array itself is `description`'s
   * 16 KB problem with no upper bound, on a table with no per-card cap
   * (`FR-CARD-022` records that Trello documents none).
   *
   * ⚠ **Empty is the ordinary state**, on `checklists`' and `labels`' footing:
   * every card carries `attachments: []` until somebody attaches something,
   * there is no seed and no backfill, and the array is never omitted — a
   * client must not read absence and emptiness as the same thing.
   *
   * ---------------------------------------------------------------------
   * ⚠⚠ `z.lazy(() => attachmentListSchema)`, NOT `attachmentListSchema`
   * DIRECTLY — THIS IS A MODULE-CYCLE FIX, MEASURED RATHER THAN GUESSED AT
   * ---------------------------------------------------------------------
   * `./attachment` already imports `cardListRefSchema` **from this file**
   * (`relatedCardSchema.list`, `BE-27.5`), so this field makes the two
   * modules import each other. Under real ESM that is fine as long as the
   * cross-imported bindings are read lazily; it is NOT fine when both
   * modules read the other's binding while building a schema at their own
   * TOP LEVEL, which `cardDetailSchema.extend({ attachments:
   * attachmentListSchema })` would have done. **Measured, not assumed**:
   * with a direct (non-lazy) reference, a throwaway spec importing
   * `./attachment` before `./card-detail` (the entry order Vitest happens
   * to use for some file orderings, and the order a future consuming
   * module could just as easily pick) produced `cardDetailSchema.shape.
   * attachments === undefined` — the key present, the schema silently
   * missing, no error anywhere, and every response this shape serialises
   * would have started dropping the field it advertises. Importing
   * `attachment.ts` FIRST forces it to resolve `cardListRefSchema` by
   * re-entering this file mid-evaluation (before this file has reached its
   * own `attachments` field, deep at the bottom), and by the time control
   * returns here, `./attachment`'s OWN later exports have not been reached
   * either — the direction that breaks depends only on which module a
   * caller happens to load first, which makes it exactly the kind of bug
   * that passes in one test order and 500s in another.
   *
   * `z.lazy()` defers the reference to when Zod actually PARSES a payload,
   * not when this schema is BUILT — by then the whole module graph has
   * long since finished loading and `attachmentListSchema` is the real
   * schema regardless of which file loaded first. Confirmed against both
   * load orders and against `z.toJSONSchema(cardDetailSchema, { io:
   * 'output' })` (the same conversion `fastify-type-provider-zod` runs for
   * OpenAPI), which resolves the lazy wrapper correctly.
   *
   * ⚠ **Do not "simplify" this back to a direct reference** because it
   * happens to typecheck and pass a test suite that loads `./card-detail`
   * before `./attachment` — that is the load order that currently hides
   * the bug, not evidence it is fixed. And do not "fix" it by having
   * `./attachment` stop importing `cardListRefSchema` from here instead:
   * that field is this file's `list: cardListRefSchema` reused rather than
   * restated (`relatedCardSchema`'s own docstring), and duplicating a
   * three-field shape to avoid one `z.lazy()` call would trade a build-time
   * safeguard for a drift risk with no safeguard at all.
   */
  attachments: z.lazy(() => attachmentListSchema),
  stickers: z.lazy(() => placedStickerListSchema),
  customFields: z.array(cardCustomFieldSchema),
  /** PR-list approvals — see `./card-approval`. Readable by anyone who can read the card. */
  approvals: cardApprovalsSchema,
});

export type CardDetail = z.infer<typeof cardDetailSchema>;
